import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ChartBar, Users, Clock, Table, TrendUp, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function ServerPerformancePage() {
  const { restId } = useRestaurant();
  const [performance, setPerformance] = useState([]);
  const [days, setDays] = useState(7);
  const [selectedServer, setSelectedServer] = useState(null);
  const [serverHistory, setServerHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchPerformance = useCallback(async () => {
    try {
      const { data } = await api.get(`/analytics/server-performance?days=${days}`);
      setPerformance(data);
    } catch { toast.error("Failed to load performance data"); }
  }, [days]);

  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);

  const openHistory = async (srv) => {
    setSelectedServer(srv);
    try {
      const { data } = await api.get(`/data/server-history?server_id=${srv.server_id}&days=30`);
      setServerHistory(data);
      setShowHistory(true);
    } catch { toast.error("Failed to load history"); }
  };

  const totals = performance.reduce((a, s) => ({
    covers: a.covers + s.total_covers,
    turns: a.turns + s.total_turns,
    tables: a.tables + s.total_tables,
  }), { covers: 0, turns: 0, tables: 0 });

  // Chart data
  const chartData = performance.map((s) => ({
    name: s.server_name,
    Covers: s.total_covers,
    Turns: s.total_turns,
    "Avg Turn": s.avg_turn_minutes,
  }));

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="server-performance-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ChartBar size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Server Performance</h2>
          <Badge className="bg-red-100 text-red-700 text-[10px]">Admin Only</Badge>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30].map((d) => (
            <button key={d} className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${days === d ? "bg-[#2F4F4F] text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`} onClick={() => setDays(d)} data-testid={`perf-days-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Covers</p>
          <p className="text-3xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>{totals.covers}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Turns</p>
          <p className="text-3xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#E07A5F" }}>{totals.turns}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Active Servers</p>
          <p className="text-3xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#81B29A" }}>{performance.length}</p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-lg border border-stone-200 p-5 mb-6" data-testid="perf-chart">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Comparison</h3>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#57534E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                <Bar dataKey="Covers" fill="#2F4F4F" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Turns" fill="#E07A5F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Server Cards */}
      <div className="space-y-3">
        {performance.map((s, idx) => (
          <div key={s.server_id} className="bg-white rounded-lg border border-stone-200 p-4 animate-fade-in" style={{ animationDelay: `${idx * 50}ms`, borderLeftWidth: "4px", borderLeftColor: s.color }} data-testid={`perf-card-${s.server_id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: s.color }}>
                  {s.server_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-semibold text-stone-800" style={{ fontFamily: "Work Sans" }}>{s.server_name}</h4>
                  <p className="text-xs text-stone-500">{s.total_tables} tables · {s.total_seats} seats</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => openHistory(s)} className="text-xs h-7" data-testid={`history-btn-${s.server_id}`}>
                <TrendUp size={14} className="mr-1" /> History
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t border-stone-100">
              <div className="text-center">
                <p className="text-[10px] text-stone-500 uppercase">Covers</p>
                <p className="text-lg font-bold" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>{s.total_covers}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-stone-500 uppercase">Turns</p>
                <p className="text-lg font-bold" style={{ fontFamily: "Work Sans", color: "#E07A5F" }}>{s.total_turns}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-stone-500 uppercase">Avg Turn</p>
                <p className="text-lg font-bold" style={{ fontFamily: "Work Sans", color: "#F4A261" }}>{s.avg_turn_minutes > 0 ? `${s.avg_turn_minutes}m` : "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-stone-500 uppercase">Reservations</p>
                <p className="text-lg font-bold text-stone-800" style={{ fontFamily: "Work Sans" }}>{s.completed_reservations}</p>
              </div>
            </div>
          </div>
        ))}
        {performance.length === 0 && (
          <div className="text-center py-12">
            <ChartBar size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
            <p className="text-stone-500 text-sm">No server data yet. Add servers and process reservations to see performance metrics.</p>
          </div>
        )}
      </div>

      {/* Server History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg" data-testid="server-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedServer && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedServer.color }} />}
              {selectedServer?.server_name} — 30 Day History
            </DialogTitle>
          </DialogHeader>
          {serverHistory && (
            <div className="mt-2 space-y-4">
              <div className="flex items-center gap-3 text-xs text-stone-500">
                <span><strong>{serverHistory.sections?.length || 0}</strong> sections</span>
                <span><strong>{serverHistory.table_count}</strong> tables</span>
              </div>
              {serverHistory.daily?.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-6">No activity in the last 30 days</p>
              ) : (
                <div className="max-h-[300px] overflow-y-auto space-y-1.5">
                  {serverHistory.daily.map((d) => (
                    <div key={d.date} className="flex items-center justify-between p-2.5 rounded-lg border border-stone-100 text-sm">
                      <span className="font-medium text-stone-700">{d.date}</span>
                      <div className="flex items-center gap-3 text-xs text-stone-500">
                        <span><Users size={12} className="inline mr-0.5" />{d.covers} covers</span>
                        <span><Table size={12} className="inline mr-0.5" />{d.reservations} res</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
